import React from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import { useAuth } from '../../context/auth'
import { useAlerts } from '../../context/alerts'
import { getDefaultCurrency, updateBusinessDetails } from '../../api'
import { getDefaultCurrencyRequestUrl } from '../../lib/defaultCurrencyUrl'
import { resolveBusinessCurrencyCode } from '../../lib/visitorCurrency'
import Input from '../ui/input'
import Checkbox from '../ui/input/checkbox'
import Button, { ButtonVariant } from '../ui/button'
import CurrencySelector from '../invoices/currencySelector'
import InvoiceOverlayModal from '../modals'

export type BusinessDetailsDraft = {
  'businessDetails.name': string
  'businessDetails.email': string
  'businessDetails.billingAddress': string
  'businessDetails.taxRegistrationNumber': string
  'businessDetails.currency.code': string
  'businessDetails.enableTax': boolean
  'businessDetails.tax.name': string
  'businessDetails.tax.percentage': string | number
}

export type BusinessModalInitialFocus = 'tradingName' | 'taxName'

export type BusinessModalProps = {
  open: boolean
  onClose: () => void
  onSaved: (draft: BusinessDetailsDraft) => void | Promise<void>
  invoiceCurrencyCode?: string
  initialFocus?: BusinessModalInitialFocus
}

const buildDraftFromUser = (
  user: ReturnType<typeof useAuth>['user'],
  invoiceCurrencyCode?: string
): BusinessDetailsDraft => ({
  'businessDetails.name': user?.businessDetails?.name || '',
  'businessDetails.email': user?.businessDetails?.email || user?.email || '',
  'businessDetails.billingAddress': user?.businessDetails?.billingAddress || '',
  'businessDetails.taxRegistrationNumber': user?.businessDetails?.taxRegistrationNumber || '',
  'businessDetails.currency.code': resolveBusinessCurrencyCode({
    savedCode: user?.businessDetails?.currency?.code,
    invoiceCode: invoiceCurrencyCode,
  }),
  'businessDetails.enableTax': !!user?.businessDetails?.enableTax,
  'businessDetails.tax.name': user?.businessDetails?.tax?.name || '',
  'businessDetails.tax.percentage': user?.businessDetails?.tax?.percentage || '',
})

const BusinessModal: React.FC<BusinessModalProps> = ({
  open,
  onClose,
  onSaved,
  invoiceCurrencyCode,
  initialFocus = 'tradingName',
}) => {
  const { user, mutate: mutateUser } = useAuth()
  const { addAlert } = useAlerts()
  const { trigger: triggerUpdateBusinessDetails, isMutating: isSaving } = useSWRMutation(
    '/api/user/business',
    updateBusinessDetails,
  )
  const { data: visitorDefaultCurrency } = useSWR(getDefaultCurrencyRequestUrl(), getDefaultCurrency)

  const [businessDetailsDraft, setBusinessDetailsDraft] = React.useState<BusinessDetailsDraft>(() =>
    buildDraftFromUser(user, invoiceCurrencyCode)
  )
  const [businessOptionalExpanded, setBusinessOptionalExpanded] = React.useState(false)
  const prevOpen = React.useRef(false)

  React.useEffect(() => {
    if (user?.businessDetails?.currency?.code) {
      return
    }
    if (!visitorDefaultCurrency?.code) {
      return
    }
    setBusinessDetailsDraft((d) => ({
      ...d,
      'businessDetails.currency.code': resolveBusinessCurrencyCode({
        savedCode: user?.businessDetails?.currency?.code,
        invoiceCode: invoiceCurrencyCode,
        defaultCode: visitorDefaultCurrency.code,
      }),
    }))
  }, [user?.businessDetails?.currency?.code, visitorDefaultCurrency?.code, invoiceCurrencyCode])

  React.useEffect(() => {
    if (open) return
    setBusinessDetailsDraft(buildDraftFromUser(user, invoiceCurrencyCode))
  }, [user, open, invoiceCurrencyCode])

  React.useEffect(() => {
    if (open && !prevOpen.current) {
      if (initialFocus === 'taxName') {
        setBusinessDetailsDraft((d) => ({
          ...d,
          'businessDetails.enableTax': true,
        }))
        setBusinessOptionalExpanded(true)
      } else {
        const d = businessDetailsDraft
        const hasOptional = Boolean(
          String(d['businessDetails.billingAddress'] || '').trim()
            || String(d['businessDetails.taxRegistrationNumber'] || '').trim()
            || d['businessDetails.enableTax']
            || String(d['businessDetails.tax.name'] || '').trim()
            || String(d['businessDetails.tax.percentage'] || '').trim()
        )
        setBusinessOptionalExpanded(hasOptional)
      }
    }
    prevOpen.current = open
    // Intentionally only when modal opens/closes — draft is read from the render that opened it.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- businessDetailsDraft
  }, [open, initialFocus])

  const businessEnableTax = businessDetailsDraft['businessDetails.enableTax']

  React.useEffect(() => {
    if (!open || initialFocus !== 'taxName') return
    const timer = window.setTimeout(() => {
      document.getElementById('businessDetails.tax.name')?.focus()
    }, 0)
    return () => window.clearTimeout(timer)
  }, [open, initialFocus, businessOptionalExpanded, businessEnableTax])

  const saveBusinessDetails = async () => {
    if (!businessDetailsDraft['businessDetails.currency.code']) {
      addAlert({ type: 'error', message: 'Please select a currency' })
      return
    }
    const result = await triggerUpdateBusinessDetails({ body: businessDetailsDraft })
    if (!result) {
      addAlert({ type: 'error', message: 'Could not update business details' })
      return
    }
    await mutateUser()
    await onSaved(businessDetailsDraft)
    onClose()
    addAlert({ type: 'info', message: 'Business details updated' })
  }

  if (!open) return null

  return (
    <InvoiceOverlayModal open={open} onClose={onClose} title='Your business details'>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          saveBusinessDetails()
        }}
      >
        <div className='mb-3'>
          <Input
            autoFocus={initialFocus === 'tradingName'}
            label='Trading Name'
            id='businessDetails.name'
            name='businessDetails.name'
            value={businessDetailsDraft['businessDetails.name']}
            onChange={(e: any) => setBusinessDetailsDraft({ ...businessDetailsDraft, [e.target.name]: e.target.value })}
            required
          />
        </div>
        <div className='mb-3'>
          <Input
            label='Email'
            id='businessDetails.email'
            name='businessDetails.email'
            type='email'
            value={businessDetailsDraft['businessDetails.email']}
            onChange={(e: any) => setBusinessDetailsDraft({ ...businessDetailsDraft, [e.target.name]: e.target.value })}
            required
          />
        </div>
        <div className='mb-3'>
          <CurrencySelector
            label='Currency'
            onChange={(e: any) => setBusinessDetailsDraft({ ...businessDetailsDraft, [e.target.name]: e.target.value })}
            value={businessDetailsDraft['businessDetails.currency.code']}
          />
        </div>
        <div className='mb-3'>
          <button
            type='button'
            className='btn btn-link p-0 text-decoration-underline'
            onClick={() => setBusinessOptionalExpanded((o) => !o)}
            aria-expanded={businessOptionalExpanded}
          >
            {businessOptionalExpanded ? 'Hide optional fields' : 'See optional fields'}
          </button>
        </div>
        {businessOptionalExpanded && (
          <>
            <div className='mb-3'>
              <Input
                label='Address'
                id='businessDetails.billingAddress'
                name='businessDetails.billingAddress'
                value={businessDetailsDraft['businessDetails.billingAddress']}
                onChange={(e: any) => setBusinessDetailsDraft({ ...businessDetailsDraft, [e.target.name]: e.target.value })}
                optional
              />
            </div>
            <div className='mb-3'>
              <Input
                label='Tax Number'
                id='businessDetails.taxRegistrationNumber'
                name='businessDetails.taxRegistrationNumber'
                value={businessDetailsDraft['businessDetails.taxRegistrationNumber']}
                onChange={(e: any) => setBusinessDetailsDraft({ ...businessDetailsDraft, [e.target.name]: e.target.value })}
                optional
              />
            </div>
            <div className='mb-2'>
              <Checkbox
                label='Add Tax To Invoices'
                id='businessDetails.enableTax'
                name='businessDetails.enableTax'
                checked={!!businessDetailsDraft['businessDetails.enableTax']}
                defaultValue={businessDetailsDraft['businessDetails.enableTax']}
                onChange={(e: any) => setBusinessDetailsDraft({ ...businessDetailsDraft, [e.target.name]: e.target.checked })}
              />
            </div>
            {businessDetailsDraft['businessDetails.enableTax'] && (
              <div className='row mb-3'>
                <div className='col-12 col-md-7'>
                  <Input
                    label='Tax Name'
                    id='businessDetails.tax.name'
                    name='businessDetails.tax.name'
                    placeholder='IVA, VAT, GST'
                    value={businessDetailsDraft['businessDetails.tax.name']}
                    onChange={(e: any) => setBusinessDetailsDraft({ ...businessDetailsDraft, [e.target.name]: e.target.value })}
                    optional
                  />
                </div>
                <div className='col-12 col-md-5 mt-2 mt-md-0'>
                  <Input
                    label='Tax %'
                    type='number'
                    id='businessDetails.tax.percentage'
                    name='businessDetails.tax.percentage'
                    value={businessDetailsDraft['businessDetails.tax.percentage']}
                    onChange={(e: any) => setBusinessDetailsDraft({ ...businessDetailsDraft, [e.target.name]: e.target.value })}
                    optional
                  />
                </div>
              </div>
            )}
          </>
        )}
        <div className='d-flex justify-content-between pt-2'>
          <Button type='button' variant={ButtonVariant.LIGHT} onClick={onClose}>
            Close
          </Button>
          <Button type='submit' loading={isSaving}>
            Save
          </Button>
        </div>
      </form>
    </InvoiceOverlayModal>
  )
}

export default BusinessModal
